import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";

/**
 * Buying a booster through real Stripe Checkout — the piece `PurchaseCatalogCard.jsx`
 * originally had inline (its own "subscribe" call plus its own "did we just come back
 * from Stripe" effect) and that Promote (`admin/crm/promote/PromoteModal.jsx`, C3.04)
 * needs a second, independent copy of for the SAME booster (`solunary_network_listing`,
 * repurposed as the marketplace-promotion unlock). One hook, so a wire change to
 * `POST /tenant/billing/booster/checkout` is one edit, not two call sites that drift.
 *
 * TWO CONCERNS, KEPT TOGETHER ON PURPOSE. Initiating the checkout and reading the return
 * are the same round trip split across a page navigation — a caller that only wired one
 * half would either never redirect or never learn what Stripe answered. Both halves stay
 * generic: neither knows what "activated" should DO for its caller (reload a catalogue?
 * reopen a modal at a given step?) — that is `onReturn`'s job, bound per caller in its own
 * language and its own screen.
 *
 * @param {object} [opts]
 * @param {(status: {status: "completed"|"blocked"|"cancelled", code?: string, kind?: string}) => void} [opts.onReturn]
 *   Fired at most once per mount, when a Stripe return is detected on the CURRENT url
 *   (`?purchase=success&session_id=…` → one read of `GET /tenant/billing/purchase/status/
 *   {sid}`, or `?purchase=cancel`). The `purchase`/`session_id` query params are stripped
 *   from the url in every case (success, blocked, cancelled), whether or not `onReturn`
 *   is given, so a reload never re-asks and never re-fires.
 * @returns {{purchase: (boosterId: string, opts?: {returnPath?: string}) => Promise<object>, busy: string|null}}
 *   `purchase` POSTs the checkout, redirects the tab to Stripe when the answer carries a
 *   `checkout_url` (the call then never resolves — the page is navigating away), or
 *   resolves with the activation body directly (demo mode: no real Stripe, the booster is
 *   turned on synchronously). It throws the raw axios error on refusal — every existing
 *   caller already owns its own localized error copy (`billingErrorMessage` in the
 *   `admin` namespace, `crmErrorMessage` in `crm`), so this hook does not pick one.
 */
export default function useBoosterCheckout({ onReturn } = {}) {
  const [busy, setBusy] = useState(null);

  // The Stripe-return read. Runs once per mount; a component that is remounted (a modal
  // closing and reopening, a route change) reads the CURRENT url again, which is correct
  // — the params are gone after the first successful strip, so there is nothing to react
  // to on any later mount.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const stripReturnParams = () => {
      const url = new URL(window.location.href);
      url.searchParams.delete("purchase");
      url.searchParams.delete("session_id");
      window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
    };
    if (params.get("purchase") === "success" && params.get("session_id")) {
      const sid = params.get("session_id");
      api.get(`/tenant/billing/purchase/status/${sid}`)
        .then(({ data }) => onReturn?.(data))
        .catch(() => {})
        .finally(stripReturnParams);
    } else if (params.get("purchase") === "cancel") {
      onReturn?.({ status: "cancelled" });
      stripReturnParams();
    }
    // Deliberately once per mount: `onReturn` is a fresh closure every render (it closes
    // over the caller's own state setters) and re-running this on every one of ITS
    // changes would re-read a url that no longer carries the return params.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const purchase = useCallback(async (boosterId, { returnPath } = {}) => {
    setBusy(boosterId);
    try {
      /* no-catch: this hook is shared by two callers (PurchaseCatalogCard.jsx,
         PromoteModal.jsx) with two different toast vocabularies (`admin`/`bill.*` vs
         `crm`/`errors.*`); the refusal propagates uncaught so EACH caller reports it in
         its own words — both already wrap this call in their own try/catch. */
      const { data } = await api.post("/tenant/billing/booster/checkout", {
        booster_id: boosterId,
        origin_url: window.location.origin,
        ...(returnPath ? { return_path: returnPath } : {}),
      });
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
        return data; // navigating away; nothing left for the caller to do with this
      }
      return data; // demo mode — already active, no redirect
    } finally {
      setBusy(null);
    }
  }, []);

  return { purchase, busy };
}
